/**
 * Nmbrs Leave Request List
 *
 * Table of submitted leave requests with sync status indicators,
 * retry functionality, and delete confirmation.
 * Story 3.3: Verlofaanvraag Indienen
 *
 * Design system:
 * - Navy #2F5A7A: headers, primary buttons
 * - Teal #54949E: pending badge, secondary buttons
 * - Green #B7D945: synced badge
 * - Red #D64545: failed badge, delete button
 * - Border #E2E8F0: table borders
 * - Text #1E293B: primary text
 */

import { useState } from 'react';
import { useLinkedEmployees } from '../hooks/useNmbrs';
import {
  useLocalLeaveRequests,
  useDeleteLeaveRequest,
  useRetryLeaveSync,
} from '../hooks/useNmbrsLeave';
import type { LeaveRequestResult } from '../hooks/useNmbrsLeave';

// ============================================
// Types
// ============================================

type SyncStatusFilter = 'all' | 'pending' | 'synced' | 'failed';

// ============================================
// Helpers
// ============================================

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ============================================
// Sub-components
// ============================================

function SyncStatusBadge({ status, error }: { status: LeaveRequestResult['syncStatus']; error: string | null }) {
  if (status === 'synced') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#B7D945]/20 text-[#2F5A7A] border border-[#B7D945]/40">
        Gesynchroniseerd
      </span>
    );
  }

  if (status === 'pending') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#54949E]/15 text-[#2F5A7A] border border-[#54949E]/30">
        In behandeling
      </span>
    );
  }

  // Story 9.1, AC-11 / FR-23: verlof dat de loonadministratie nooit bereikt is
  // geen achterstand en geen fout — er is niets te doen, dus er staat niets.
  // Deze tak MOET vóór de mislukt-tak hieronder staan: die is de restwaarde en
  // zou `niet_boekend` anders als rode "Mislukt"-badge tonen, precies het
  // "er moet nog iets gebeuren"-signaal dat hier verboden is.
  if (status === 'niet_boekend') {
    return null;
  }

  // failed — show error tooltip
  return (
    <div className="relative group inline-block">
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#D64545]/10 text-[#D64545] border border-[#D64545]/30 cursor-help">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
        </svg>
        Mislukt
      </span>
      {error && (
        <div className="absolute bottom-full left-0 mb-1 w-64 bg-[#1E293B] text-white text-xs rounded-lg p-2 hidden group-hover:block z-10 shadow-lg">
          {error}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="px-4 py-10 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      </div>
      <p className="text-sm text-gray-500">Geen verlofaanvragen gevonden</p>
      <p className="text-xs text-gray-400 mt-1">Ingediende aanvragen verschijnen hier.</p>
    </div>
  );
}

// ============================================
// Row component
// ============================================

function LeaveRequestRow({
  request,
  employeeName,
  onDelete,
  onRetry,
  isDeleting,
  isRetrying,
}: {
  request: LeaveRequestResult;
  employeeName: string;
  onDelete: (id: number) => void;
  onRetry: (id: number) => void;
  isDeleting: boolean;
  isRetrying: boolean;
}) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const isBusy = isDeleting || isRetrying;

  return (
    <tr className="border-b border-[#E2E8F0] hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3 text-sm text-[#1E293B]">{employeeName}</td>
      <td className="px-4 py-3 text-sm text-[#1E293B]">{request.leaveTypeName}</td>
      <td className="px-4 py-3 text-sm text-[#1E293B] whitespace-nowrap">
        {formatDate(request.startDate)} – {formatDate(request.endDate)}
      </td>
      <td className="px-4 py-3 text-sm text-[#1E293B] text-right">{request.hours} u</td>
      <td className="px-4 py-3">
        <SyncStatusBadge status={request.syncStatus} error={request.syncError} />
      </td>
      <td className="px-4 py-3 text-sm text-gray-500">#{request.submittedBy}</td>
      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
        {formatDateTime(request.submittedAt)}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 justify-end">
          {(request.syncStatus === 'failed' || request.syncStatus === 'pending') && (
            <button
              onClick={() => onRetry(request.id)}
              disabled={isBusy}
              className="px-2 py-1 text-xs font-medium text-[#2F5A7A] bg-[#2F5A7A]/10 rounded hover:bg-[#2F5A7A]/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              title={request.syncStatus === 'pending' ? 'Sync opnieuw starten' : 'Sync opnieuw proberen'}
            >
              {isRetrying ? 'Bezig...' : 'Opnieuw'}
            </button>
          )}

          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isBusy}
              className="px-2 py-1 text-xs font-medium text-[#D64545] bg-[#D64545]/10 rounded hover:bg-[#D64545]/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Verwijderen"
            >
              Verwijder
            </button>
          ) : (
            <div className="flex items-center gap-1">
              <span className="text-xs text-[#D64545]">Zeker?</span>
              <button
                onClick={() => {
                  onDelete(request.id);
                  setShowDeleteConfirm(false);
                }}
                disabled={isBusy}
                className="px-2 py-1 text-xs font-medium text-white bg-[#D64545] rounded hover:bg-[#b83b3b] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isDeleting ? 'Bezig...' : 'Ja'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isBusy}
                className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Nee
              </button>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

// ============================================
// Employee leave requests panel
// ============================================

function EmployeeRequestsPanel({
  employeeId,
  employeeName,
  statusFilter,
}: {
  employeeId: number;
  employeeName: string;
  statusFilter: SyncStatusFilter;
}) {
  const { data, isLoading, error } = useLocalLeaveRequests(employeeId);
  const deleteMutation = useDeleteLeaveRequest();
  const retryMutation = useRetryLeaveSync();

  const allRequests = data?.data ?? [];
  const filtered =
    statusFilter === 'all'
      ? allRequests
      : allRequests.filter((r) => r.syncStatus === statusFilter);

  if (isLoading) {
    return (
      <tr>
        <td colSpan={8} className="px-4 py-8 text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <svg className="w-4 h-4 animate-spin text-[#54949E]" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Laden...
          </div>
        </td>
      </tr>
    );
  }

  if (error) {
    return (
      <tr>
        <td colSpan={8} className="px-4 py-4 text-center text-sm text-[#D64545]">
          Fout bij ophalen aanvragen
        </td>
      </tr>
    );
  }

  if (filtered.length === 0) {
    return null;
  }

  return (
    <>
      {filtered.map((request) => (
        <LeaveRequestRow
          key={request.id}
          request={request}
          employeeName={employeeName}
          onDelete={(id) => deleteMutation.mutate(id)}
          onRetry={(id) => retryMutation.mutate(id)}
          isDeleting={deleteMutation.isPending && deleteMutation.variables === request.id}
          isRetrying={retryMutation.isPending && retryMutation.variables === request.id}
        />
      ))}
    </>
  );
}

// ============================================
// Main Component
// ============================================

export function NmbrsLeaveRequestList() {
  const { data: linksData, isLoading: linksLoading } = useLinkedEmployees();
  const [statusFilter, setStatusFilter] = useState<SyncStatusFilter>('all');

  const linkedEmployees = linksData?.data ?? [];

  const filterOptions: Array<{ value: SyncStatusFilter; label: string }> = [
    { value: 'all', label: 'Alle statussen' },
    { value: 'pending', label: 'In behandeling' },
    { value: 'synced', label: 'Gesynchroniseerd' },
    { value: 'failed', label: 'Mislukt' },
  ];

  return (
    <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#E2E8F0] flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold text-[#2F5A7A]">Ingediende verlofaanvragen</h3>
          <p className="text-xs text-gray-500 mt-0.5">Overzicht van alle verlofaanvragen en hun Nmbrs synchronisatiestatus</p>
        </div>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as SyncStatusFilter)}
          className="px-3 py-1.5 border border-[#E2E8F0] rounded-lg text-sm text-[#1E293B] focus:ring-2 focus:ring-[#54949E] focus:border-[#54949E]"
        >
          {filterOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      {linksLoading ? (
        <div className="p-8 text-center">
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-8 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
      ) : linkedEmployees.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50 border-b border-[#E2E8F0]">
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Medewerker
                </th>
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Type
                </th>
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Periode
                </th>
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide text-right">
                  Uren
                </th>
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Sync Status
                </th>
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Ingediend door
                </th>
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Datum
                </th>
                <th className="px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide text-right">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody>
              {linkedEmployees.map((link) => (
                <EmployeeRequestsPanel
                  key={link.employeeId}
                  employeeId={link.employeeId}
                  employeeName={link.employeeName}
                  statusFilter={statusFilter}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
